import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import axiosInstance from '../../api/axiosConfig';
import './FormStyles.css';
import receptionBg from '../../assets/images/backgrounds/reception-bg.png';

const InterviewForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const isPublic = location.pathname.startsWith('/visitor/form');

    const [formData, setFormData] = useState({
        candidateName: '',
        email: '',
        phone: '',
        position: '',
        interviewerName: '',
        department: '',
        interviewTime: '',
        resumeSubmitted: 'yes',
        notes: ''
    });

    const [resumeFile, setResumeFile] = useState(null);
    const [resumePreview, setResumePreview] = useState('');

    const API_URL = process.env.REACT_APP_API_URL || '';

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg'];
            if (!allowedTypes.includes(file.type)) {
                alert('Only PDF and JPG files are allowed');
                e.target.value = '';
                return;
            }

            // Validate file size (5MB max)
            if (file.size > 5 * 1024 * 1024) {
                alert('File size must be less than 5MB');
                e.target.value = '';
                return;
            }

            setResumeFile(file);
            setResumePreview(file.name);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Create FormData for file upload
            const submitData = new FormData();
            submitData.append('formType', 'interview');
            submitData.append('formData', JSON.stringify(formData));
            submitData.append('notes', formData.notes);
            
            // Add resume file if selected
            if (resumeFile) {
                submitData.append('resume', resumeFile);
            }

            if (isPublic) {
                await axios.post(`${API_URL}/api/public/visitor-forms`, submitData);
            } else {
                await axiosInstance.post('/api/forms', submitData);
            }

            setSuccess(true);
            setTimeout(() => {
                navigate(isPublic ? '/visitor' : '/purpose-selection');
            }, 2000);
        } catch (error) {
            alert('Error submitting form: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate(isPublic ? '/visitor' : '/purpose-selection');
    };

    return (
        <div className="form-container" style={{ backgroundImage: `url(${receptionBg})` }}>
            <div className="form-wrapper card fade-in">
                <div className="form-header">
                    <img src="/logo.png" alt="Logo" className="app-logo" />
                    <div className="form-header-text">
                        <h2>Interview Candidate Registration</h2>
                        <p>Job applicant check-in</p>
                    </div>
                    <div className="form-header-actions">
                        {!isPublic && user && (
                            <div className="form-user-info">
                                <strong>{user?.fullName || user?.username}</strong>
                            </div>
                        )}
                        <button
                            type="button"
                            className="btn-back-form"
                            onClick={handleBack}
                        >
                            ← Back
                        </button>
                    </div>
                </div>

                {success && (
                    <div className="alert alert-success">
                        ✅ Candidate registered successfully! Redirecting...
                    </div>
                )}

                <form onSubmit={handleSubmit} className="visitor-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="candidateName" className="form-label">Candidate Name *</label>
                            <input
                                type="text"
                                id="candidateName"
                                name="candidateName"
                                className="form-input"
                                value={formData.candidateName}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email" className="form-label">Email Address *</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                className="form-input"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="phone" className="form-label">Phone Number *</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                className="form-input"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="position" className="form-label">Position Applied *</label>
                            <input
                                type="text"
                                id="position"
                                name="position"
                                className="form-input"
                                value={formData.position}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="interviewerName" className="form-label">Interviewer Name *</label>
                            <input
                                type="text"
                                id="interviewerName"
                                name="interviewerName"
                                className="form-input"
                                value={formData.interviewerName}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="department" className="form-label">Department</label>
                            <input
                                type="text"
                                id="department"
                                name="department"
                                className="form-input"
                                value={formData.department}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="interviewTime" className="form-label">Interview Time</label>
                            <input
                                type="time"
                                id="interviewTime"
                                name="interviewTime"
                                className="form-input"
                                value={formData.interviewTime}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="resumeSubmitted" className="form-label">Resume Submitted</label>
                            <select
                                id="resumeSubmitted"
                                name="resumeSubmitted"
                                className="form-select"
                                value={formData.resumeSubmitted}
                                onChange={handleChange}
                            >
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="resume" className="form-label">Upload Resume (PDF or JPG)</label>
                        <input
                            type="file"
                            id="resume"
                            name="resume"
                            className="form-input"
                            accept=".pdf,.jpg,.jpeg"
                            onChange={handleFileChange}
                        />
                        {resumePreview && (
                            <small style={{ color: '#28a745', marginTop: '5px', display: 'block' }}>
                                ✓ Selected: {resumePreview}
                            </small>
                        )}
                        <small style={{ color: '#6c757d', marginTop: '5px', display: 'block' }}>
                            Accepted formats: PDF, JPG (Max size: 5MB)
                        </small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="notes" className="form-label">Additional Notes</label>
                        <textarea
                            id="notes"
                            name="notes"
                            className="form-textarea"
                            value={formData.notes}
                            onChange={handleChange}
                            rows="3"
                        ></textarea>
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleBack}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading ? 'Submitting...' : 'Register Candidate'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default InterviewForm;
